import { useContext, useEffect, useState } from 'react';
import { AppContext } from 'contexts/AppContext';
import { useQualityData } from 'hooks/useQualityData';
import { exportCsv } from 'lib/exportCsv';
import { formatUtcDateTime } from 'utils/helperFunctions';
import { Button } from 'components/ui/button';
import QueryCategoriesCard from 'components/monitoring/QueryCategoriesCard';
import { Card, CardContent, CardHeader, CardTitle } from 'components/ui/card';
import { Badge } from 'components/ui/badge';
import {
  AlertTriangle, Activity, ChevronLeftIcon, ChevronRightIcon,
  ThumbsDown, ThumbsUp, Info, Wrench, MessageSquareQuote, Download } from 'lucide-react';

/**
 * Quality — where the agent fails, and how often.
 *
 * Two cards, matching the redesign mock:
 *   Null / unhelpful responses  — the failing turns, classified
 *   Response quality            — the headline rates
 *
 * The mock's own design language is followed for anything unmeasurable: a
 * dimmed row with a "needs events" note, rather than a zero or a blank. Three
 * figures land there, and the reason is recorded next to each.
 */

/**
 * A metric the app does not record yet. Dimmed, labelled, never shown as 0 --
 * a zero would read as "measured, and it is nothing", which is a stronger claim
 * than the data supports.
 *
 * The label is "Not tracked yet" rather than "Coming soon" on purpose: nobody
 * owns this work and there is no date, so "soon" would be a promise the roadmap
 * cannot keep. "Not tracked yet" says the same thing without the commitment.
 */
function PendingStat({ label, reason }) {
  return (
    <div className="flex items-start justify-between gap-4 py-2 border-b border-zinc-200 dark:border-zinc-800 last:border-0 opacity-55">
      <div className="min-w-0">
        <div className="text-sm flex items-center gap-2">
          {label}
          <Badge variant="outline" className="font-normal text-[10px] px-1.5 py-0 whitespace-nowrap">
            Not Tracked Yet
          </Badge>
        </div>
        <div className="text-xs text-gray-500 mt-0.5">{reason}</div>
      </div>
      <span className="text-sm text-gray-400 shrink-0">—</span>
    </div>
  );
}

function Stat({ label, value, sub, tone, onClick }) {
  const colour = tone === 'good' ? 'text-emerald-600' : tone === 'bad' ? 'text-red-600' : '';
  return (
    <div
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      className={`flex items-start justify-between gap-4 py-2 border-b border-zinc-200 dark:border-zinc-800 last:border-0${onClick ? ' cursor-pointer hover:bg-zinc-50 dark:hover:bg-zinc-900 -mx-2 px-2 rounded' : ''}`}>
      <div className="min-w-0">
        <div className="text-sm">{label}</div>
        {sub && <div className="text-xs text-gray-500 mt-0.5">{sub}</div>}
      </div>
      <span className={`text-sm font-medium shrink-0 ${colour}`}>
        {value === null || value === undefined ? <span className="text-gray-400 font-normal">—</span> : value}
      </span>
    </div>
  );
}

/**
 * Two signals, both evidence of an actual failure.
 *
 * `query_category = out_of_scope` was tried here first and removed: measured
 * against live data it marks the TOPIC, not the outcome. Answers like "who do
 * I ask for at the Vista branch?" carry it and are perfectly good replies to
 * questions that simply are not about the catalog. Listing them here would
 * overstate failure, which is the one direction this tab must not err in.
 */
const CLASS_META = {
  agent_failure: {
    label: 'agent said it could not help',
    cls: 'bg-red-50 text-red-700 border-red-300 dark:bg-red-950/40 dark:text-red-200',
  },
  user_rejected: {
    label: 'user thumbed it down',
    cls: 'bg-amber-50 text-amber-800 border-amber-300 dark:bg-amber-950/40 dark:text-amber-200',
  },
};

// Timestamps render in UTC, the zone the date filter uses -- see helperFunctions.
const fmtTime = (v) => formatUtcDateTime(v);

const pct = (v) => (v === null || v === undefined ? null : `${v}%`);

/**
 * Slowest tool by p95, not by average.
 *
 * Measured 2026-08-10: generate_questions AVERAGES 2.16s and peaks at 60.44s.
 * Ranking by average puts it eighth and the headline reads 4.22s, so the tool
 * that actually made someone wait a minute never surfaces. Averages hide the
 * tail, and the tail is the only part of a latency distribution a user feels.
 *
 * p95 rather than max because a max is one event and may be a fluke; p95 says
 * "one call in twenty is at least this slow", which is robust AND actionable.
 */
const slowestTool = (tools) =>
  (tools || [])
    .filter((t) => t.p95_seconds !== null && t.p95_seconds !== undefined)
    .sort((a, b) => b.p95_seconds - a.p95_seconds)[0] || null;

export default function QualityTab() {
  const {
    selectedClient, dateRange, platformFilter, languageFilter,
    setActiveTab, setSessionsPrefill,
  } = useContext(AppContext);

  // The thumbs figures ARE conversations; clicking one opens them.
  const openSessions = (feedback) => {
    setSessionsPrefill({ feedback });
    setActiveTab('Sessions');
  };

  // Same click-through for the categories card: hand Sessions the raw stored
  // value (or the '(uncategorised)' sentinel) and it lists the conversations
  // containing at least one turn on that topic.
  const openCategory = (category) => {
    setSessionsPrefill({ category });
    setActiveTab('Sessions');
  };
  const {
    metrics, metricsLoading, metricsError, fetchMetrics,
    unhelpful, unhelpfulTotal, unhelpfulLoading, unhelpfulError,
    page, pageSize, fetchUnhelpful,
    tools, toolsLoading, fetchTools,
    categories, categoriesLoading, fetchCategories,
    written, writtenTotal, writtenLoading, writtenPage, fetchWritten,
  } = useQualityData();

  const [classification, setClassification] = useState('');
  // Same audience control as the other tabs, applied to EVERY figure at once
  // -- a toggle that reached the counts but not the lists would be the
  // half-honored-filter bug all over again.
  const [audience, setAudience] = useState('all');
  const excludeInternal = audience === 'customers';
  const internalOnly = audience === 'internal';
  const slowest = slowestTool(tools);

  useEffect(() => {
    fetchMetrics({ excludeInternal, internalOnly });
    fetchUnhelpful({ classification, pageIndex: 0, excludeInternal, internalOnly });
    fetchTools({ excludeInternal, internalOnly });
    fetchWritten({ pageIndex: 0, excludeInternal, internalOnly });
    fetchCategories({ excludeInternal, internalOnly });
  }, [selectedClient, dateRange, classification, platformFilter, languageFilter,
      audience, excludeInternal, internalOnly,
      fetchMetrics, fetchUnhelpful, fetchTools, fetchWritten, fetchCategories]);

  const filterBtn = (value, label) => (
    <Button
      size="sm"
      variant={classification === value ? 'default' : 'outline'}
      onClick={() => setClassification(classification === value ? '' : value)}
    >
      {label}
    </Button>
  );

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 items-start">

      <div className="flex items-center gap-2 lg:col-span-2 order-1">
        {[
          { v: 'all', l: 'All Traffic' },
          { v: 'customers', l: 'Customers Only' },
          { v: 'internal', l: 'Internal Only' },
        ].map((o) => (
          <Button key={o.v} size="sm"
                  variant={audience === o.v ? 'default' : 'outline'}
                  onClick={() => setAudience(o.v)}>
            {o.l}
          </Button>
        ))}
        <span className="text-xs text-gray-500">
          {audience === 'customers'
            ? 'Excluding internal accounts from every figure on this tab.'
            : audience === 'internal'
              ? 'Internal accounts only — every figure on this tab.'
              : 'Including internal accounts.'}
        </span>
      </div>

      {/* ── Null / unhelpful responses ─────────────────────────────────── */}
      <Card className="min-w-0 order-3">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <AlertTriangle className="h-4 w-4" />
            Null / Unhelpful Responses
            {unhelpful.length > 0 && (
              <Button size="sm" variant="outline" className="ml-auto font-normal"
                      onClick={() => exportCsv('unhelpful-responses', unhelpful)}>
                <Download className="h-3.5 w-3.5 mr-1" />CSV
              </Button>
            )}
          </CardTitle>
          <div className="flex flex-wrap gap-2 pt-3">
            {filterBtn('agent_failure', 'Agent failure')}
            {filterBtn('user_rejected', 'Thumbed down')}
          </div>
        </CardHeader>

        <CardContent className="px-0">
          {unhelpfulError && <div className="px-6 py-4 text-sm text-red-600">{unhelpfulError}</div>}
          {unhelpfulLoading && <div className="px-6 py-8 text-sm text-gray-500">Loading…</div>}

          {!unhelpfulLoading && !unhelpfulError && unhelpful.length === 0 && (
            <div className="px-6 py-8 text-sm text-gray-500">
              No unhelpful responses matched in this range.
            </div>
          )}

          {!unhelpfulLoading && unhelpful.length > 0 && (
            <ul className="divide-y divide-zinc-200 dark:divide-zinc-800">
              {unhelpful.map((r, i) => {
                const meta = CLASS_META[r.classification];
                return (
                  <li key={r.message_id || i} className="px-6 py-3">
                    <div className="text-sm font-medium mb-1 whitespace-pre-wrap">
                      {r.input || <span className="italic text-gray-400">(no question recorded)</span>}
                    </div>
                    <div className="text-xs text-gray-600 dark:text-gray-300 mb-2 line-clamp-2 whitespace-pre-wrap">
                      {r.output}
                    </div>
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500">
                      {meta
                        ? <span className={`px-2 py-0.5 rounded border ${meta.cls}`}>{meta.label}</span>
                        : <Badge variant="outline" className="font-normal">{r.classification}</Badge>}
                      {r.query_category && <span>{r.query_category}</span>}
                      {r.feedback === -1 && (
                        <span className="inline-flex items-center gap-1 text-red-600">
                          <ThumbsDown className="h-3 w-3" />thumbs down
                        </span>
                      )}
                      <span>{fmtTime(r.time_stamp)}</span>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}

          <div className="flex items-center justify-between px-6 pt-4">
            <span className="text-xs text-gray-500">
              {unhelpfulTotal > 0
                ? `${page * pageSize + 1}–${page * pageSize + unhelpful.length} of ${unhelpfulTotal}`
                : ''}
            </span>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" disabled={page === 0 || unhelpfulLoading}
                onClick={() => fetchUnhelpful({ classification, pageIndex: page - 1, excludeInternal, internalOnly })}>
                <ChevronLeftIcon className="h-4 w-4" />
              </Button>
              <Button size="sm" variant="outline"
                disabled={unhelpfulLoading || (page + 1) * pageSize >= unhelpfulTotal}
                onClick={() => fetchUnhelpful({ classification, pageIndex: page + 1, excludeInternal, internalOnly })}>
                <ChevronRightIcon className="h-4 w-4" />
              </Button>
            </div>
          </div>

          <p className="px-6 pt-4 text-xs text-gray-500 flex gap-2">
            <Info className="h-3.5 w-3.5 shrink-0 mt-0.5" />
            <span>
              <strong>Agent failure</strong> is matched on response wording — a deliberately narrow
              heuristic that undercounts rather than overstates. <strong>Thumbed down</strong> is the
              user's own verdict. Off-catalog questions are <em>not</em> listed here: the agent answers
              most of them well, and counting them would inflate the failure rate.
            </span>
          </p>
        </CardContent>
      </Card>

      {/* ── Response quality ───────────────────────────────────────────── */}
      <Card className="min-w-0 order-1">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Activity className="h-4 w-4" />
            Response Quality
          </CardTitle>
        </CardHeader>

        <CardContent>
          {metricsError && <div className="text-sm text-red-600 py-2">{metricsError}</div>}
          {metricsLoading && <div className="text-sm text-gray-500 py-8">Loading…</div>}

          {!metricsLoading && metrics && (
            <>
              <Stat
                label={<span className="inline-flex items-center gap-1.5"><ThumbsUp className="h-3.5 w-3.5" />Thumbs up</span>}
                value={pct(metrics.thumbs_up_pct)}
                sub={metrics.rated_total ? `${metrics.thumbs_up} of ${metrics.rated_total} rated — click to read them` : 'no ratings in range'}
                tone="good"
                onClick={metrics.thumbs_up ? () => openSessions('positive') : undefined}
              />
              <Stat
                label={<span className="inline-flex items-center gap-1.5"><ThumbsDown className="h-3.5 w-3.5" />Thumbs down</span>}
                value={pct(metrics.thumbs_down_pct)}
                sub={metrics.rated_total ? `${metrics.thumbs_down} of ${metrics.rated_total} rated — click to read them` : null}
                tone="bad"
                onClick={metrics.thumbs_down ? () => openSessions('negative') : undefined}
              />
              {metrics.rated_session_pct !== null && metrics.rated_session_pct < 15 && (
                <div className="flex gap-2 my-2 rounded-md border border-amber-300 bg-amber-50 dark:bg-amber-950/30 p-2.5 text-xs text-amber-900 dark:text-amber-200">
                  <Info className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                  <span>
                    {/* rated_sessions, not rated_total: the numerator counts
                        SESSIONS carrying a rating, matching the sentence. The
                        old pairing counted rated messages, so one user rating
                        eight turns of one conversation read as eight rated
                        sessions and the coverage claim could exceed 100%. */}
                    Only <strong>{metrics.rated_session_pct}%</strong> of sessions carry a rating
                    ({metrics.rated_sessions ?? metrics.rated_total} of {metrics.sessions}). The
                    percentages above describe those, not all conversations.
                  </span>
                </div>
              )}
              {metrics.voice_turns > 0 && (
                <div className="flex gap-2 my-2 rounded-md border border-zinc-300 dark:border-zinc-700 p-2.5 text-xs text-gray-600 dark:text-gray-300">
                  <Info className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                  <span>
                    These figures include <strong>{metrics.voice_turns}</strong> voice
                    turn{metrics.voice_turns === 1 ? '' : 's'}. Quality does not separate
                    modalities yet, so voice conversations count toward turns-per-session and
                    single-turn figures alongside text.
                  </span>
                </div>
              )}
              <Stat
                label="Single-turn Sessions"
                value={pct(metrics.single_turn_session_pct)}
                sub={`${metrics.single_turn_sessions} of ${metrics.sessions} — asked once, did not continue`}
              />
              <Stat
                label="Avg Turns per Session"
                value={metrics.avg_turns_per_session}
              />
              {/* The stop button. adk-v2 has logged it all along (metadata
                  `cancelled: true`, key omitted otherwise); nothing read it
                  until now. Turns predating the instrumentation read as
                  not-cancelled, so old ranges undercount rather than err. */}
              {metrics.total_turns != null && (
                <Stat
                  label="Stopped Mid-Answer"
                  value={pct(metrics.cancelled_turn_pct)}
                  sub={`${metrics.cancelled_turns} of ${metrics.total_turns} turns — the user hit stop`}
                  tone={metrics.cancelled_turn_pct >= 10 ? 'bad' : undefined}
                />
              )}

              {/* Absent by measurement, not by omission. The mock marks these
                  "new"; the reason each one is unavailable is stated rather
                  than left as a bare dash. */}
              <PendingStat
                label="Abandon Rate"
                reason="The app doesn't record when someone opens the assistant, so we can't tell who opened it and left without asking. Single-turn sessions above is the closest thing we can measure."
              />
              <PendingStat
                label="Follow-up Rate"
                reason="Nothing marks which messages are replies, so a follow-up to an answer looks identical to a brand-new question."
              />
              {/* Was a PendingStat. It should never have been: the durations
                  were already in metadata.function_calls. See the Tool
                  performance card below for the per-tool breakdown. */}
              <Stat
                label="Slowest Tool (p95)"
                value={slowest ? `${slowest.p95_seconds}s` : null}
                sub={slowest
                  ? `${slowest.tool} — 1 call in 20 is at least this slow · avg ${slowest.avg_seconds}s, worst ${slowest.max_seconds}s`
                  : 'no timed tool calls in range'}
                tone={slowest && slowest.p95_seconds >= 10 ? 'bad' : undefined}
              />
            </>
          )}
        </CardContent>
      </Card>

      {/* ── Tool performance ───────────────────────────────────────────── */}
      <Card className="min-w-0 order-2">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Wrench className="h-4 w-4" />
            Tool Performance
          </CardTitle>
        </CardHeader>
        <CardContent>
          {toolsLoading && <div className="text-sm text-gray-500 py-6">Loading…</div>}
          {!toolsLoading && tools.length === 0 && (
            <div className="text-sm text-gray-500 py-6">
              No tool calls recorded in this range.
            </div>
          )}
          {!toolsLoading && tools.length > 0 && (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs text-gray-500 border-b border-zinc-200 dark:border-zinc-800">
                  <th className="text-left font-normal py-1.5">Tool</th>
                  <th className="text-right font-normal">Calls</th>
                  <th className="text-right font-normal">Success</th>
                  <th className="text-right font-normal">Avg</th>
                  <th className="text-right font-normal">p95</th>
                  <th className="text-right font-normal">Worst</th>
                </tr>
              </thead>
              <tbody>
                {tools.map((t) => (
                  <tr key={t.tool} className="border-b border-zinc-100 dark:border-zinc-900 last:border-0">
                    <td className="py-1.5 pr-2 truncate max-w-[190px]" title={t.tool}>{t.tool}</td>
                    <td className="text-right tabular-nums">{t.calls}</td>
                    <td className={`text-right tabular-nums ${t.success_pct !== null && t.success_pct < 90 ? 'text-red-600' : ''}`}>
                      {pct(t.success_pct) ?? '—'}
                    </td>
                    <td className="text-right tabular-nums">
                      {t.avg_seconds !== null ? `${t.avg_seconds}s` : <span className="text-gray-400">—</span>}
                    </td>
                    <td className={`text-right tabular-nums ${t.p95_seconds !== null && t.p95_seconds >= 10 ? 'text-red-600 font-medium' : ''}`}>
                      {t.p95_seconds !== null ? `${t.p95_seconds}s` : <span className="text-gray-400">—</span>}
                    </td>
                    <td className="text-right tabular-nums text-gray-500">
                      {t.max_seconds !== null ? `${t.max_seconds}s` : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <p className="pt-3 text-xs text-gray-500 flex gap-2">
            <Info className="h-3.5 w-3.5 shrink-0 mt-0.5" />
            <span>
              Read from data already stored per call. A dash means that entry records a
              decision rather than a timed call, so there is no duration. <strong>p95</strong>
              is the number to watch — an average hides the tail, and the tail is the part
              users actually feel.
            </span>
          </p>
        </CardContent>
      </Card>

      {/* ── What people ask about ──────────────────────────────────────── */}
      <div className="order-5 lg:col-span-2">
        <QueryCategoriesCard data={categories} loading={categoriesLoading}
                             onSelect={openCategory} />
      </div>

      {/* ── Written feedback ───────────────────────────────────────────── */}
      <Card className="min-w-0 order-4">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <MessageSquareQuote className="h-4 w-4" />
            {/* Not "what users said went wrong". The query behind this filters
                on a non-empty `feedback_message` with NO restriction on the
                feedback sign (quality_retrieval.get_written_feedback), so
                praise lands here too and was being presented as a complaint. */}
            What users wrote
            {written.length > 0 && (
              <Button size="sm" variant="outline" className="ml-auto font-normal"
                      onClick={() => exportCsv('written-feedback', written)}>
                <Download className="h-3.5 w-3.5 mr-1" />CSV
              </Button>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="px-0">
          {writtenLoading && <div className="px-6 py-6 text-sm text-gray-500">Loading…</div>}
          {!writtenLoading && written.length === 0 && (
            <div className="px-6 py-6 text-sm text-gray-500">
              Nobody typed feedback in this range.
            </div>
          )}
          {!writtenLoading && written.length > 0 && (
            <ul className="divide-y divide-zinc-200 dark:divide-zinc-800">
              {written.map((r, i) => (
                <li key={r.message_id || i} className="px-6 py-3">
                  <div className="text-sm font-medium mb-1 flex items-start gap-2">
                    {/* The sign was already being selected and thrown away.
                        Showing it is what actually makes the card neutral — a
                        renamed heading alone still leaves a compliment sitting
                        in an amber quote block reading like a complaint. */}
                    {r.feedback === 1 && (
                      <ThumbsUp className="h-3.5 w-3.5 mt-0.5 shrink-0 text-emerald-600" aria-label="Positive" />
                    )}
                    {r.feedback === -1 && (
                      <ThumbsDown className="h-3.5 w-3.5 mt-0.5 shrink-0 text-red-600" aria-label="Negative" />
                    )}
                    <span className="min-w-0">{r.input}</span>
                  </div>
                  {r.feedback_message && (
                    <div className={`text-xs mb-1 pl-3 border-l-2 whitespace-pre-wrap ${
                      r.feedback === 1
                        ? 'border-emerald-400 text-emerald-900 dark:text-emerald-200'
                        : 'border-amber-400 text-amber-900 dark:text-amber-200'
                    }`}>
                      “{r.feedback_message}”
                    </div>
                  )}
                  {r.correct_answer && (
                    <div className="text-xs mb-1 pl-3 border-l-2 border-emerald-400 text-emerald-900 dark:text-emerald-200 whitespace-pre-wrap">
                      Should have said: {r.correct_answer}
                    </div>
                  )}
                  <div className="text-xs text-gray-500">{fmtTime(r.time_stamp)}</div>
                </li>
              ))}
            </ul>
          )}
          <div className="flex items-center justify-between px-6 pt-4">
            <span className="text-xs text-gray-500">
              {writtenTotal > 0
                ? `${writtenPage * pageSize + 1}–${writtenPage * pageSize + written.length} of ${writtenTotal}`
                : ''}
            </span>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" disabled={writtenPage === 0 || writtenLoading}
                onClick={() => fetchWritten({ pageIndex: writtenPage - 1, excludeInternal, internalOnly })}>
                <ChevronLeftIcon className="h-4 w-4" />
              </Button>
              <Button size="sm" variant="outline"
                disabled={writtenLoading || (writtenPage + 1) * pageSize >= writtenTotal}
                onClick={() => fetchWritten({ pageIndex: writtenPage + 1, excludeInternal, internalOnly })}>
                <ChevronRightIcon className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
